import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faBlog,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { apiFetch } from '../config';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { SkeletonTechGrid, SkeletonJobTimeline } from './Skeleton';
import { ErrorRetry } from './ErrorRetry';

// Convert FA key (e.g. "faDocker") to CSS classes ("fa-brands fa-docker")
// Falls back to solid prefix if brands doesn't match
function iconKeyToClass(key) {
  if (!key) return '';
  const name = key.replace(/^fa/, '').replace(/([A-Z])/g, '-$1').toLowerCase();
  return `fa-brands fa-${name.replace(/^-/, '')}`;
}

function AboutMe() {
  useDocumentTitle('About');
  const [technologies, setTechnologies] = useState([]);
  const [experience, setExperience] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [techLoading, setTechLoading] = useState(true);
  const [expLoading, setExpLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [techError, setTechError] = useState(null);
  const [expError, setExpError] = useState(null);

  const fetchTechnologies = useCallback(() => {
    setTechLoading(true);
    setTechError(null);
    apiFetch('/technologies')
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        setTechnologies(data);
      })
      .catch(error => {
        console.error('Error fetching technologies:', error);
        setTechError('Unable to load technologies.');
      })
      .finally(() => setTechLoading(false));
  }, []);

  const fetchJobs = useCallback(() => {
    setExpLoading(true);
    setExpError(null);
    apiFetch('/jobs')
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        const sortedData = data.sort((a, b) => b - a).reverse();
        setExperience(sortedData);
      })
      .catch(error => {
        console.error('Error fetching jobs:', error);
        setExpError('Unable to load experience timeline.');
      })
      .finally(() => setExpLoading(false));
  }, []);

  const fetchRecentPosts = useCallback(() => {
    setPostsLoading(true);
    apiFetch('/posts')
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => b.id - a.id).slice(0, 3);
          setRecentPosts(sorted);
        }
      })
      .catch(error => {
        console.error('Error fetching recent posts:', error);
      })
      .finally(() => setPostsLoading(false));
  }, []);

  useEffect(() => {
    fetchTechnologies();
  }, [fetchTechnologies]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchRecentPosts();
  }, [fetchRecentPosts]);

  // Spotlight grid glow — single gradient follows cursor across the entire grid
  const gridRef = useRef(null);
  const [spotlight, setSpotlight] = useState({ x: 0, y: 0, visible: false });

  const handleGridMouseMove = useCallback((e) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;
    setSpotlight({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
  }, []);

  const handleGridMouseLeave = useCallback(() => {
    setSpotlight(prev => ({ ...prev, visible: false }));
  }, []);

  const [heroRef, heroVisible] = useScrollReveal();
  const [designRef, designVisible] = useScrollReveal();

  // Data-load-triggered animations
  const [techAnimateIn, setTechAnimateIn] = useState(false);
  const [expAnimateIn, setExpAnimateIn] = useState(false);

  useEffect(() => {
    if (!techLoading && technologies.length > 0) {
      requestAnimationFrame(() => setTechAnimateIn(true));
    }
  }, [techLoading, technologies]);

  useEffect(() => {
    if (!expLoading && experience.length > 0) {
      requestAnimationFrame(() => setExpAnimateIn(true));
    }
  }, [expLoading, experience]);

  const techDelay = (index) => `${index * 60}ms`;
  const expDelay = (index) => `${index * 120}ms`;

  return (
    <section className="px-6 py-4 page-enter">
      {/* Hero */}
      <div ref={heroRef} className={`relative rounded-2xl bg-gradient-to-br from-purple-dark/40 via-black to-black border border-white/5 p-8 mb-10 overflow-hidden ${heroVisible ? 'reveal-hero' : 'reveal-hidden'}`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="flex flex-col sm:flex-row items-center gap-8 relative z-10">
          <img className="block rounded-full w-36 h-36 object-cover ring-4 ring-gold/30 shadow-[0_0_30px_rgba(255,187,0,0.1)] flex-shrink-0" src={process.env.PUBLIC_URL + '/myPicture.jpg'} alt="Alexandru Chiscari" width="144" height="144" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-1">Alexandru Chiscari</h1>
            <p className="text-gold font-bold text-base mb-4 mt-0">DevOps Specialist · Photographer · Designer</p>
            <p className="text-sm text-gray-light mb-0 leading-relaxed">
              Infrastructure & DevOps specialist focused on enterprise OpenShift, distributed container
              architectures, and resilient active-active platforms. Combining technical precision with a
              creative eye from design and photography to build clean, observable, and scalable systems.
            </p>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex items-center gap-4 mb-10 flex-wrap">
        <Link to="/gallery" className="btn no-underline inline-flex items-center">
          <FontAwesomeIcon icon={faCamera} className="mr-2" />Photo Gallery
        </Link>
        <Link to="/blog" className="btn no-underline inline-flex items-center">
          <FontAwesomeIcon icon={faBlog} className="mr-2" />Blog
        </Link>
      </div>

      {/* Latest from the Blog */}
      {(!postsLoading || recentPosts.length > 0) && (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-heading mb-0">Latest from the Blog</h2>
            <Link
              to="/blog"
              className="text-xs font-bold text-gold hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              View all posts <FontAwesomeIcon icon={faArrowRight} size="xs" />
            </Link>
          </div>

          {postsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="card animate-pulse h-36 bg-white/[0.02] border border-white/5 rounded-xl"></div>
              ))}
            </div>
          ) : recentPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recentPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="card group hover:border-gold/50 transition-all duration-200 flex flex-col justify-between block no-underline border border-white/10 hover:shadow-[0_4px_20px_rgba(255,187,0,0.08)] bg-white/[0.02] rounded-xl p-4.5"
                >
                  <div>
                    {post.categories && post.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2.5">
                        {post.categories.slice(0, 2).map((cat, ci) => (
                          <span
                            key={ci}
                            className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-white/5 text-gray-medium border border-white/10 group-hover:border-gold/30 group-hover:text-gold transition-colors"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3 className="text-sm font-bold text-white group-hover:text-gold transition-colors mb-2 line-clamp-2 leading-snug">
                      {post.title}
                    </h3>
                  </div>
                  <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-gold/80 font-semibold group-hover:text-gold transition-colors mt-3">
                    <span>Read post</span>
                    <FontAwesomeIcon icon={faArrowRight} className="transform group-hover:translate-x-1 transition-transform" size="xs" />
                  </div>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      )}

      <h2 className={`section-heading ${techAnimateIn ? 'section-heading-animated' : ''}`}>Technologies I use</h2>
      {techLoading ? (
        <SkeletonTechGrid />
      ) : techError ? (
        <ErrorRetry message={techError} onRetry={fetchTechnologies} />
      ) : (
        <div
          ref={gridRef}
          onMouseMove={handleGridMouseMove}
          onMouseLeave={handleGridMouseLeave}
          className="relative grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3 max-[768px]:grid-cols-[repeat(auto-fill,minmax(140px,1fr))]"
        >
          {/* Spotlight overlay */}
          <div
            className="pointer-events-none absolute -inset-px rounded-xl transition-opacity duration-300 z-0"
            style={{
              opacity: spotlight.visible ? 1 : 0,
              background: `radial-gradient(400px circle at ${spotlight.x}px ${spotlight.y}px, rgba(255,187,0,0.07), transparent 60%)`,
            }}
          />
          {technologies.map((tech, index) => (
            <div key={index} className={`tech-item group relative z-10 flex flex-col items-center justify-center p-4 border border-white/10 rounded-xl bg-white/[0.02] transition-all duration-300 hover:text-gold hover:border-gold/30 hover:bg-gold/[0.04] max-[960px]:flex-row max-[960px]:pl-6 max-[960px]:h-16 max-[960px]:justify-start max-[768px]:flex-row max-[768px]:pl-4 max-[768px]:h-16 max-[768px]:justify-start stagger-item ${techAnimateIn ? 'revealed' : ''}`} style={{ animationDelay: techDelay(index)}}>
              <i className={`${iconKeyToClass(tech.icon)} fa-2x transition-transform duration-300 group-hover:scale-110`} />
              <p className="text-xs m-0 mt-2 text-gray-light max-[960px]:mt-0 max-[960px]:ml-3 max-[768px]:mt-0 max-[768px]:ml-3">{tech.name}</p>
            </div>
          ))}
        </div>
      )}

      <h2 className={`section-heading ${designVisible ? 'section-heading-animated' : ''}`}>Design principles</h2>
      <div ref={designRef} className={`card border-l-2 border-l-gold/40 pl-6 ${designVisible ? 'reveal-left' : 'reveal-hidden'}`}>
        <p className="text-base mb-0">
          I am a firm believer in the power of simplicity in design. To me, a simple design is not just about aesthetics, but also about efficiency. By eliminating unnecessary elements, we can focus on what truly matters and make the user experience smoother and more intuitive. This approach not only makes the design more appealing but also improves the overall performance of the system. I strive to apply this principle in all my projects, always aiming to create solutions that are not only effective but also efficient and easy to use.
        </p>
      </div>

      <h2 className={`section-heading ${expAnimateIn ? 'section-heading-animated' : ''}`}>Experience</h2>
      {expLoading ? (
        <div className="space-y-4 pl-6 border-l border-gold/20">
          {[...Array(3)].map((_, i) => (
            <SkeletonJobTimeline key={i} />
          ))}
        </div>
      ) : expError ? (
        <ErrorRetry message={expError} onRetry={fetchJobs} />
      ) : (
        <div className="relative pl-6 border-l border-gold/20">
          {experience.map((job, index) => (
            <div key={index} className={`relative card mb-4 last:mb-0 stagger-item ${expAnimateIn ? 'revealed' : ''}`} style={{ animationDelay: expDelay(index) }}>
              <div className={`absolute -left-[calc(1.5rem+5px)] top-6 w-2.5 h-2.5 rounded-full bg-gold shadow-[0_0_8px_rgba(255,187,0,0.4)] ${expAnimateIn ? 'timeline-dot-animated' : ''}`}></div>
              <h3 className="text-base mb-0.5">{job.title} <span className="text-gray-medium font-normal">at</span> {job.company}</h3>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-medium italic">{job.duration}</span>
                <span className="text-xs text-gold/60 font-medium">{job.location}</span>
              </div>
              <p className="text-sm mb-0">{job.description}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default AboutMe;
